import React, { useEffect, useMemo, useState } from 'react';
import ModalPortal from './ModalPortal';
import {
  createCustomStandardSet,
  updateCustomStandardSet,
  deleteCustomStandardSet,
  fetchStandardsProgressionData,
} from '../services/api';

const SUBJECT_LIST = ['Mathematics', 'Science', 'Literacy', 'Social Studies', 'Success Skills'];
const GRADE_BANDS = ['PK', 'K-5', '6-8', '9-12'];
const BAND_GRADES = {
  'PK': ['PK'],
  'K-5': ['PK', 'K', '1', '2', '3', '4', '5'],
  '6-8': ['6', '7', '8', '6-8'],
  '9-12': ['9', '10', '11', '12', '9-10', '11-12', '9-12'],
};
const ALL_GRADES = ['PK', 'K', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
// Stable default so the standards-loading effect does not refetch on every render.
const NO_STANDARDS = [];

function standardMatches(std, subject, gradeBand) {
  const bandSet = new Set((BAND_GRADES[gradeBand] || ALL_GRADES).map((g) => String(g).toUpperCase()));
  const matchSubject = !subject || String(std.subject).toLowerCase() === String(subject).toLowerCase();
  const stdGrade = String(std.grade_level || std.grade || '').toUpperCase().replace(/^(\d+)\s+TO\s+(\d+)$/i, '$1-$2');
  const matchGrade = !stdGrade || bandSet.has(stdGrade) || bandSet.has(stdGrade.split('-')[0]);
  return matchSubject && matchGrade;
}
const SET_TYPES = [
  { value: 'unit', label: 'Unit' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'trimester', label: 'Trimester' },
  { value: 'semester', label: 'Semester' },
  { value: 'custom', label: 'Custom' },
];

export default function StandardSetModal({
  isOpen,
  onClose,
  initialSubject = 'Mathematics',
  initialGradeBand = 'K-5',
  availableStandards = NO_STANDARDS,
  editingSet = null,
  onSaved,
  onDeleted,
}: any) {
  const [subject, setSubject] = useState(initialSubject);
  const [gradeBand, setGradeBand] = useState(initialGradeBand);
  const [timeFrameType, setTimeFrameType] = useState('unit');
  const [timeFrameLabel, setTimeFrameLabel] = useState<any>('');
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState<any>('');
  const [allStandards, setAllStandards] = useState<any>([]);
  const [loadingStandards, setLoadingStandards] = useState<any>(false);
  const [saving, setSaving] = useState<any>(false);
  const [deleting, setDeleting] = useState<any>(false);
  const [error, setError] = useState<any>('');

  // Sync state when modal opens or editingSet changes
  useEffect(() => {
    if (!isOpen) return;

    setError('');
    setSearchTerm('');

    if (editingSet) {
      setSubject(editingSet.subject || initialSubject);
      setGradeBand(editingSet.grade_band || initialGradeBand);
      setTimeFrameType(editingSet.time_frame_type || 'unit');
      setTimeFrameLabel(editingSet.time_frame_label || '');
      setSelectedCodes(new Set(editingSet.mls_codes || []));
    } else {
      setSubject(initialSubject);
      setGradeBand(initialGradeBand);
      setTimeFrameType('unit');
      setTimeFrameLabel('');
      setSelectedCodes(new Set());
    }
  }, [isOpen, editingSet, initialSubject, initialGradeBand]);

  // Load standard database if availableStandards is empty
  useEffect(() => {
    if (!isOpen) return;

    if (availableStandards && availableStandards.length > 0) {
      setAllStandards(availableStandards);
      return;
    }

    let cancelled = false;
    setLoadingStandards(true);
    fetchStandardsProgressionData()
      .then((data) => {
        if (!cancelled) setAllStandards(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setAllStandards([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingStandards(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, availableStandards]);

  // Filter standards by current subject & grade band
  const filteredStandards = useMemo(
    () => allStandards.filter((std) => standardMatches(std, subject, gradeBand)),
    [allStandards, subject, gradeBand],
  );

  // Hide grade bands with no standards for the subject (e.g. Social Studies has no PK).
  const availableGradeBands = useMemo(() => {
    if (!allStandards.length) return GRADE_BANDS;
    const bands = GRADE_BANDS.filter((band) => (band === gradeBand && editingSet)
      || allStandards.some((std) => standardMatches(std, subject, band)));
    return bands.length ? bands : GRADE_BANDS;
  }, [allStandards, subject, gradeBand, editingSet]);

  useEffect(() => {
    if (!editingSet && !availableGradeBands.includes(gradeBand)) setGradeBand(availableGradeBands[0]);
  }, [availableGradeBands, gradeBand, editingSet]);

  // Further filter by user search term
  const searchedStandards = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return filteredStandards;

    return filteredStandards.filter((std) => {
      const code = String(std.mls_code || std.code || '').toLowerCase();
      const desc = String(std.standard_description || std.text || std.cluster_expectation_description || '').toLowerCase();
      return code.includes(term) || desc.includes(term);
    });
  }, [filteredStandards, searchTerm]);

  const toggleCode = (code) => {
    if (!code) return;
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const selectAllShown = () => {
    const next = new Set(selectedCodes);
    searchedStandards.forEach((std) => {
      const code = std.mls_code || std.code;
      if (code) next.add(code);
    });
    setSelectedCodes(next);
  };

  const deselectAllShown = () => {
    const next = new Set(selectedCodes);
    searchedStandards.forEach((std) => {
      const code = std.mls_code || std.code;
      if (code) next.delete(code);
    });
    setSelectedCodes(next);
  };

  const handleSave = async () => {
    if (!timeFrameLabel.trim()) {
      setError('Please enter a name for this Standard Set (e.g. "Unit 1: Place Value").');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const codes = Array.from(selectedCodes);
      let result;

      if (editingSet?.id) {
        result = await updateCustomStandardSet(editingSet.id, {
          timeFrameType,
          timeFrameLabel: timeFrameLabel.trim(),
          mlsCodes: codes,
        });
      } else {
        result = await createCustomStandardSet({
          subject,
          gradeBand,
          timeFrameType,
          timeFrameLabel: timeFrameLabel.trim(),
          mlsCodes: codes,
        });
      }

      if (onSaved) {
        onSaved(result);
      }
      onClose();
    } catch (saveErr: any) {
      setError(saveErrorMsg(saveErr));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingSet?.id) return;
    if (!window.confirm(`Delete standard set "${editingSet.time_frame_label}"?`)) return;

    setDeleting(true);
    setError('');

    try {
      await deleteCustomStandardSet(editingSet.id);
      if (onDeleted) {
        onDeleted(editingSet.id);
      }
      onClose();
    } catch (delErr: any) {
      setError(saveErrorMsg(delErr));
    } finally {
      setDeleting(false);
    }
  };

  const saveErrorMsg = (err) => {
    if (typeof err === 'string') return err;
    return err?.message || 'An error occurred while saving the standard set.';
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !saving && !deleting) {
            onClose();
          }
        }}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1300,
          background: 'rgba(15, 23, 42, 0.55)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
        }}
      >
        <div
          style={{
            width: 'min(780px, 100%)',
            maxHeight: '92vh',
            overflowY: 'auto',
            background: '#ffffff',
            borderRadius: 18,
            boxShadow: '0 24px 64px rgba(15, 23, 42, 0.28)',
            padding: 22,
            border: '1px solid #dbe4f0',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#1e3a5f' }}>
                {editingSet ? 'Edit Standard Set' : 'Create Custom Standard Set'}
              </h3>
              <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>
                Define custom target sets for units, quarters, trimesters, or custom learning goals.
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={saving || deleting}
              style={{ background: 'transparent', border: 'none', fontSize: 24, color: '#64748b', cursor: 'pointer', lineHeight: 1 }}
            >
              ×
            </button>
          </div>

          {error && (
            <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: 10, fontSize: 13, fontWeight: 700 }}>
              {error}
            </div>
          )}

          {/* Form Fields Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 4 }}>
                Set Name / Label*
              </label>
              <input
                type="text"
                placeholder="e.g. Unit 1: Fractions & Decimals"
                value={timeFrameLabel}
                onChange={(e) => setTimeFrameLabel(e.target.value)}
                style={{ width: '100%', height: 42, borderRadius: 10, border: '1.5px solid #cbd5e1', padding: '0 12px', fontSize: 13, fontWeight: 700, boxSizing: 'border-box' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 4 }}>
                Set Type
              </label>
              <select
                value={timeFrameType}
                onChange={(e) => setTimeFrameType(e.target.value)}
                style={{ width: '100%', height: 42, borderRadius: 10, border: '1.5px solid #cbd5e1', padding: '0 12px', fontSize: 13, fontWeight: 700, boxSizing: 'border-box', background: '#fff' }}
              >
                {SET_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 4 }}>
                Subject
              </label>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                disabled={Boolean(editingSet)}
                style={{ width: '100%', height: 42, borderRadius: 10, border: '1.5px solid #cbd5e1', padding: '0 12px', fontSize: 13, fontWeight: 700, boxSizing: 'border-box', background: editingSet ? '#f8fafc' : '#fff' }}
              >
                {SUBJECT_LIST.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 800, color: '#334155', textTransform: 'uppercase', marginBottom: 4 }}>
                Grade Band
              </label>
              <select
                value={gradeBand}
                onChange={(e) => setGradeBand(e.target.value)}
                disabled={Boolean(editingSet)}
                style={{ width: '100%', height: 42, borderRadius: 10, border: '1.5px solid #cbd5e1', padding: '0 12px', fontSize: 13, fontWeight: 700, boxSizing: 'border-box', background: editingSet ? '#f8fafc' : '#fff' }}
              >
                {availableGradeBands.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Standard Selection List */}
          <div style={{ border: '1px solid #dbe4f0', borderRadius: 12, padding: 14, background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 800, color: '#1e3a5f' }}>Select Standards to Include</span>
                <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 800, color: '#1d4ed8', background: '#eff6ff', padding: '2px 8px', borderRadius: 999, border: '1px solid #bfdbfe' }}>
                  {selectedCodes.size} selected
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={selectAllShown}
                  style={{ background: '#ffffff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                >
                  Select All Shown ({searchedStandards.length})
                </button>
                <button
                  type="button"
                  onClick={deselectAllShown}
                  style={{ background: '#ffffff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: 8, padding: '5px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
                >
                  Clear Shown
                </button>
              </div>
            </div>

            <input
              type="text"
              placeholder="Search standards by code or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%', height: 38, borderRadius: 8, border: '1px solid #cbd5e1', padding: '0 10px', fontSize: 12, boxSizing: 'border-box' }}
            />

            {loadingStandards ? (
              <div style={{ padding: 16, fontSize: 13, color: '#64748b', textAlign: 'center' }}>Loading standards...</div>
            ) : searchedStandards.length === 0 ? (
              <div style={{ padding: 16, fontSize: 13, color: '#64748b', textAlign: 'center' }}>
                No standards match for {subject} · {gradeBand}{searchTerm ? ` matching "${searchTerm}"` : ''}.
              </div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, background: '#ffffff', border: '1px solid #cbd5e1', borderRadius: 10, padding: 8 }}>
                {searchedStandards.map((std) => {
                  const code = std.mls_code || std.code;
                  if (!code) return null;
                  const isChecked = selectedCodes.has(code);
                  const desc = std.standard_description || std.text || std.cluster_expectation_description || '';

                  return (
                    <label
                      key={code}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '8px 10px',
                        borderRadius: 8,
                        background: isChecked ? '#eff6ff' : '#ffffff',
                        border: isChecked ? '1px solid #bfdbfe' : '1px solid #f1f5f9',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'background 0.15s',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleCode(code)}
                        style={{ marginTop: 2, cursor: 'pointer' }}
                      />
                      <div style={{ fontSize: 12, lineHeight: 1.35, color: '#1e293b' }}>
                        <span style={{ fontWeight: 800, color: '#1d4ed8', marginRight: 6 }}>{code}</span>
                        {std.grade_level || std.grade ? (
                          <span style={{ fontSize: 10, fontWeight: 800, color: '#64748b', background: '#f1f5f9', padding: '1px 6px', borderRadius: 4, marginRight: 6 }}>
                            Grade {std.grade_level || std.grade}
                          </span>
                        ) : null}
                        <span>{desc}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          {/* Modal Actions */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 4 }}>
            {editingSet?.id ? (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting || saving}
                style={{ background: '#fff1f2', color: '#b91c1c', border: '1px solid #fecdd3', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                {deleting ? 'Deleting...' : 'Delete Set'}
              </button>
            ) : (
              <div />
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                onClick={onClose}
                disabled={saving || deleting}
                style={{ background: '#ffffff', color: '#475569', border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || deleting}
                style={{ background: '#1d4ed8', color: '#ffffff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 800, cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.75 : 1 }}
              >
                {saving ? 'Saving...' : editingSet ? 'Update Standard Set' : 'Save Standard Set'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
